function parseCssPx(value) {
  const n = parseFloat(String(value || '').trim());
  return Number.isFinite(n) ? n : 0;
}

/**
 * Padding below sticky header (--header-group-height + --header-height + extraPx).
 */
function stickyHeaderAnchorOffset(extraPx = 24) {
  const root = getComputedStyle(document.documentElement);
  const groupH = parseCssPx(root.getPropertyValue('--header-group-height'));
  let headerH = parseCssPx(root.getPropertyValue('--header-height'));
  if (headerH === 0) {
    const headerEl = document.querySelector('.header-section header[is="sticky-header"]');
    if (headerEl) headerH = Math.round(headerEl.offsetHeight);
  }
  return groupH + headerH + extraPx;
}

function scrollElementBelowStickyHeader(element, behavior, extraBelowHeaderPx = 24) {
  const top =
    element.getBoundingClientRect().top + window.scrollY - stickyHeaderAnchorOffset(extraBelowHeaderPx);
  window.scrollTo({ top: Math.max(0, top), behavior });
}

if (!customElements.get('collapsible-heading-cards')) {
  customElements.define(
    'collapsible-heading-cards',
    class CollapsibleHeadingCards extends HTMLElement {
      constructor() {
        super();
        this.scrollPad = 24;
        this.carouselInstance = null;
        this.mqlMobile = null;
        this._onMedia = this.handleMediaChange.bind(this);
        this._onBlockSelect = this.onBlockSelect.bind(this);
      }

      scrollToTarget(target, behavior) {
        scrollElementBelowStickyHeader(target, behavior, this.scrollPad);
      }

      getTrack() {
        return this.querySelector('.collapsible-tabs__heading-cards-track');
      }

      getLayout() {
        return this.closest('.collapsible-tabs__heading-cards-layout');
      }

      bindCards() {
        this.querySelectorAll('a.collapsible-tabs__heading-card').forEach((card) => {
          card.addEventListener('click', (e) => {
            e.preventDefault();
            const href = card.getAttribute('href');
            if (!href || href.charAt(0) !== '#') return;
            const target = document.getElementById(href.slice(1));
            if (!target) return;
            this.setActive(card);
            this.scrollToTarget(target, 'smooth');
          });
        });
      }

      handleMediaChange() {
        if (!this.mqlMobile) return;
        if (this.mqlMobile.matches) {
          this.destroyDesktopSwiper();
        } else {
          this.initDesktopSwiper();
        }
      }

      initDesktopSwiper() {
        if (this.carouselInstance || typeof FoxTheme === 'undefined' || !FoxTheme.Carousel) {
          return;
        }
        const track = this.getTrack();
        if (!track) return;

        const slides = track.querySelectorAll('.swiper-slide');
        if (slides.length < 2) return;

        const layout = this.getLayout();
        const nextEl = layout && layout.querySelector('.swiper-button-next');
        const prevEl = layout && layout.querySelector('.swiper-button-prev');
        if (!nextEl || !prevEl) return;

        const slidesPerView = Math.min(5, slides.length);

        this.classList.add('swiper');
        track.classList.add('swiper-wrapper');

        const sliderOptions = {
          modules: [FoxTheme.Swiper.Navigation, FoxTheme.Swiper.Mousewheel],
          slidesPerView,
          spaceBetween: 20,
          navigation: { nextEl, prevEl },
          loop: false,
          threshold: 2,
          watchOverflow: true,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
          },
        };

        try {
          this.carouselInstance = new window.FoxTheme.Carousel(this, sliderOptions);
          this.carouselInstance.init();
        } catch (err) {
          this.carouselInstance = null;
          this.classList.remove('swiper');
          track.classList.remove('swiper-wrapper');
          return;
        }

        const ce = this.carouselInstance.slider;
        if (ce && FoxTheme.a11y && typeof FoxTheme.a11y.getFocusableElements === 'function') {
          FoxTheme.a11y.getFocusableElements(this).forEach((element) => {
            element.addEventListener('focusin', () => {
              const slide = element.closest('.swiper-slide');
              if (!slide || !ce.slides || !ce.slides.length) return;
              const idx = Array.prototype.indexOf.call(ce.slides, slide);
              if (idx >= 0) ce.slideTo(idx);
            });
          });
        }

        if (typeof Shopify !== 'undefined' && Shopify.designMode && this.dataset.sectionId) {
          document.addEventListener('shopify:block:select', this._onBlockSelect);
        }
      }

      onBlockSelect(e) {
        if (!this.dataset.sectionId || e.detail.sectionId !== this.dataset.sectionId) return;
        if (!this.carouselInstance || !this.carouselInstance.slider) return;
        const from =
          e.target && e.target.closest
            ? e.target.closest('.collapsible-tabs__heading-card-slide[data-index]')
            : null;
        const idx = from ? Number(from.dataset.index) : NaN;
        if (Number.isNaN(idx)) return;
        this.carouselInstance.slider.slideTo(idx);
      }

      destroyDesktopSwiper() {
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          document.removeEventListener('shopify:block:select', this._onBlockSelect);
        }
        if (!this.carouselInstance) return;

        const track = this.getTrack();
        try {
          if (this.carouselInstance.slider) {
            this.carouselInstance.slider.destroy(true, false);
          }
        } catch (err) {
          /* ignore */
        }
        this.carouselInstance = null;
        this.classList.remove('swiper');
        if (track) {
          track.classList.remove('swiper-wrapper');
        }
      }

      connectedCallback() {
        const mobileMq =
          typeof FoxTheme !== 'undefined' && FoxTheme.config && FoxTheme.config.mediaQueryMobile
            ? FoxTheme.config.mediaQueryMobile
            : 'screen and (max-width: 767px)';
        this.mqlMobile = window.matchMedia(mobileMq);
        this.mqlMobile.addEventListener('change', this._onMedia);
        this.bindCards();
        this.handleMediaChange();
        this.applyHash();
      }

      disconnectedCallback() {
        if (this.mqlMobile) {
          this.mqlMobile.removeEventListener('change', this._onMedia);
        }
        this.destroyDesktopSwiper();
      }

      setActive(activeCard) {
        this.querySelectorAll('a.collapsible-tabs__heading-card').forEach((c) => {
          const on = c === activeCard;
          c.classList.toggle('is-active', on);
          if (on) {
            c.setAttribute('aria-current', 'page');
          } else {
            c.removeAttribute('aria-current');
          }
        });
      }

      applyHash() {
        const hash = window.location.hash;
        if (!hash || hash.indexOf('#collapsible-heading-') !== 0) return;
        const target = document.getElementById(hash.slice(1));
        if (!target) return;
        const card = this.querySelector(`a.collapsible-tabs__heading-card[href="${hash}"]`);
        if (card) this.setActive(card);
        const run = () => this.scrollToTarget(target, 'auto');
        requestAnimationFrame(run);
        setTimeout(run, 150);
      }
    }
  );
}

if (!customElements.get('collapsible-tabs')) {
  customElements.define(
    'collapsible-tabs',
    class CollapsibleTabs extends HTMLElement {
      constructor() {
        super();
        this.onToggle = this.onToggle.bind(this);
      }

      get mobileMq() {
        const q =
          typeof FoxTheme !== 'undefined' && FoxTheme.config && FoxTheme.config.mediaQueryMobile
            ? FoxTheme.config.mediaQueryMobile
            : 'screen and (max-width: 767px)';
        if (!this._mobileMqCache || this._mobileMqRaw !== q) {
          this._mobileMqRaw = q;
          this._mobileMqCache = window.matchMedia(q);
        }
        return this._mobileMqCache;
      }

      get anchorBelowHeaderPx() {
        const raw = Number.parseInt(String(this.dataset.mobileAnchorBelowHeader || '').trim(), 10);
        return Number.isFinite(raw) ? raw : 50;
      }

      connectedCallback() {
        this.detailsAbort?.abort();
        this.detailsAbort = new AbortController();
        const { signal } = this.detailsAbort;

        this.detailsElements = this.querySelectorAll('.accordion-details');
        this.colorScheme = this.dataset.colorScheme;

        this.detailsElements.length > 0 &&
          this.detailsElements.forEach((details) => {
            details.open ? this.handleOpen(details) : this.handleClose(details);
            details.addEventListener('toggle', this.onToggle, { signal });

            const summary = details.querySelector('summary');
            if (summary) {
              summary.addEventListener(
                'click',
                () => {
                  if (!this.mobileMq.matches) return;
                  queueMicrotask(() =>
                    CollapsibleTabs.scrollAccordionItemBelowHeaderAfterAnimation(
                      details,
                      this.anchorBelowHeaderPx
                    )
                  );
                },
                { signal }
              );
            }
          });
      }

      /**
       * Run after accordion click handlers so `details._accordionAnimation` exists.
       */
      static scrollAccordionItemBelowHeaderAfterAnimation(details, belowHeaderPx) {
        const item = details.closest('.accordion-item');
        const target = item || details;
        const anim = details._accordionAnimation;
        const run = () => scrollElementBelowStickyHeader(target, 'smooth', belowHeaderPx);
        if (anim && typeof anim.then === 'function') {
          anim.then(run).catch(() => {});
        } else {
          requestAnimationFrame(run);
        }
      }

      disconnectedCallback() {
        this.detailsAbort?.abort();
        this.detailsAbort = null;
      }

      onToggle(event) {
        const activeDetails = event.target;
        if (activeDetails.open) {
          this.handleOpen(activeDetails);
        } else {
          this.handleClose(activeDetails);
        }
      }

      handleOpen(details) {
        const parent = details.closest('.accordion-item');
        parent.classList.contains('accordion-card') && parent.classList.add(this.colorScheme);
      }

      handleClose(details) {
        const parent = details.closest('.accordion-item');
        parent.classList.contains('accordion-card') && parent.classList.remove(this.colorScheme);
      }
    }
  );
}
